import React from "react";
import { Box, Button, Divider, Stack } from "@mui/material";

import { ControlledAutocomplete, FieldControl } from "../../../shared/components";
import { useRisk } from "../hooks/useRisk";
import { useDisplayField } from "../../../hooks/useDisplayField";

export const RaiseRisk: React.FC<{ editStatus: boolean, riskId: string | null, projectId: string | undefined, onCancel: () => void; refetch: () => void; }> = ({ editStatus, riskId, projectId, onCancel, refetch }) => {

    const { getFilterOptions } = useDisplayField([]);

    const {
        overviewData, setOverviewData,
        isSaving: saving, onSaveRisk,
        isPhasePending, phaseOptions
    } = useRisk({ editStatus, riskId, projectId, refetch: () => { refetch(); onCancel(); } });

    return (
        <Box display={'flex'} flexDirection={'column'} gap={2} height={'100%'} overflow={'hidden'}>
            <Box flex={1} sx={{ overflowY: 'auto' }} p={2}>
                <Box display={'flex'} flexDirection={'column'} gap={2}>

                    <FieldControl
                        keyName={'type'}
                        label={"Query Type"}
                        variant={"select"}
                        options={[
                            { label: 'Project', value: 'Project' },
                            { label: 'Portfolio', value: 'Portfolio' },
                            { label: 'Programme', value: 'Programme' },
                        ]}
                        value={overviewData.type}
                        onChange={(key: string, value: any) => {
                            setOverviewData({ ...overviewData, [key]: value });
                        }}
                    />

                    {
                        overviewData.type === 'Project' &&
                        <ControlledAutocomplete
                            type="PROJECT"
                            value={overviewData.project}
                            onSelect={(val: any) => setOverviewData({ ...overviewData, project: val })}
                            label="Search project"
                            placeholder="Search project"
                        />
                    }

                    {
                        overviewData.type === 'Project' &&
                        <FieldControl
                            keyName={'phase'}
                            label={"Select Project Phase"}
                            variant={"select"}
                            value={overviewData.phase}
                            options={
                                phaseOptions.map((value: { id: string, name: string; }) => {
                                    return {
                                        value: value.id, label: value.name
                                    }
                                })
                            }
                            onChange={(key: string, value: any) => {
                                setOverviewData({ ...overviewData, [key]: value });
                            }}
                            loading={isPhasePending}
                        />
                    }

                    {
                        overviewData.type === 'Portfolio' &&
                        <ControlledAutocomplete
                            type="PORTFOLIO"
                            value={overviewData.portfolio}
                            onSelect={(val: any) => setOverviewData({ ...overviewData, portfolio: val })}
                            label="Search portfolio"
                            placeholder="Search portfolio"
                        />
                    }

                    {
                        overviewData.type === 'Programme' &&
                        <ControlledAutocomplete
                            type="PROGRAMME"
                            value={overviewData.programme}
                            onSelect={(val: any) => setOverviewData({ ...overviewData, programme: val })}
                            label="Search programme"
                            placeholder="Search programme"
                        />
                    }

                    <Divider />

                    <FieldControl
                        keyName={"riskTitle"}
                        label={"Title"}
                        variant={"input"}
                        value={overviewData.riskTitle}
                        onChange={(key: string, value: any) => {
                            setOverviewData({ ...overviewData, [key]: value });
                        }}
                    />

                    <FieldControl
                        keyName={"riskDescription"}
                        label={"Risk Description"}
                        variant={"input"}
                        multiline
                        value={overviewData.riskDescription}
                        onChange={(key: string, value: any) => {
                            setOverviewData({ ...overviewData, [key]: value });
                        }}
                    />

                    <FieldControl
                        keyName={"riskCategory"}
                        label={"Category"}
                        variant={"select"}
                        options={getFilterOptions('RISK_CATEGORY').map((value) => {
                            return {
                                label: value.display_text,
                                value: value.key
                            }
                        })}
                        value={overviewData.riskCategory}
                        onChange={(key: string, value: any) => {
                            setOverviewData({ ...overviewData, [key]: value });
                        }}
                    />

                    <FieldControl
                        keyName={"riskStatus"}
                        label={"Status"}
                        variant={"select"}
                        options={getFilterOptions('RISK_OVERVIEW_STATUS').map((value) => {
                            return {
                                label: value.display_text,
                                value: value.key
                            }
                        })}
                        value={overviewData.riskStatus}
                        onChange={(key: string, value: any) => {
                            setOverviewData({ ...overviewData, [key]: value });
                        }}
                    />
                    
                    <FieldControl
                        keyName={"riskReviewDate"}
                        label={"Review Date"}
                        variant={"datetime"}
                        value={overviewData.riskReviewDate}
                        onChange={(key: string, value: any) => {
                            setOverviewData({ ...overviewData, [key]: value });
                        }}
                    />

                </Box>
            </Box>
            <Stack direction="row" spacing={2} justifyContent="flex-end" flexShrink={0} bgcolor={'action.hover'} p={2}>
                <Button onClick={onCancel} variant="outlined" loading={saving} disabled={saving}>Cancel</Button>
                <Button onClick={onSaveRisk} variant="contained" loading={saving} disabled={saving}>Save</Button>
            </Stack>
        </Box>
    );

};